import { useState } from "react";
import { apiPost } from "../../api/client";
import { useTranslation } from "../../i18n";

export const PHASES = [
  { id: "brainstorm", labelKey: "phase.brainstorm" },
  { id: "grouping", labelKey: "phase.grouping" },
  { id: "voting", labelKey: "phase.voting" },
  { id: "discussion", labelKey: "phase.discussion" },
] as const;

interface PhaseControlsProps {
  publicId: string;
  currentPhase: string;
  /** Only the owner of an ACTIVE retro may change the phase. */
  canControl: boolean;
  onChanged: () => void;
}

export function PhaseControls({
  publicId,
  currentPhase,
  canControl,
  onChanged,
}: PhaseControlsProps) {
  const { t } = useTranslation();
  const [pending, setPending] = useState<string | null>(null);
  const [error, setError] = useState("");

  const changePhase = async (phase: string) => {
    setPending(phase);
    setError("");
    try {
      await apiPost(`/api/retrospectives/${publicId}/phase`, { phase });
      onChanged();
    } catch {
      setError(t("phase.error"));
    } finally {
      setPending(null);
    }
  };

  const active = PHASES.find((phase) => phase.id === currentPhase);
  const activeLabel = active ? t(active.labelKey) : currentPhase;

  return (
    <div className="card p-4">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <span className="text-[0.7rem] font-bold uppercase tracking-[0.08em] text-ink-muted">
          {t("phase.label")}
        </span>

        {!canControl ? (
          <span className="text-lg font-bold text-ink">{activeLabel}</span>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {PHASES.map((phase) => {
              const isCurrent = phase.id === currentPhase;
              return (
                <button
                  key={phase.id}
                  onClick={() => changePhase(phase.id)}
                  disabled={isCurrent || pending !== null}
                  aria-pressed={isCurrent}
                  className={`chip ${
                    isCurrent
                      ? "chip-active"
                      : "chip-idle hover:bg-nm-primary/10 hover:text-nm-primary disabled:opacity-50"
                  }`}
                >
                  {pending === phase.id ? "…" : t(phase.labelKey)}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {error && <p className="mt-2 text-xs text-red-500">{error}</p>}
    </div>
  );
}
